import React, { useEffect, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Backdrop from "@mui/material/Backdrop";
import { StyledEngineProvider, ThemeProvider } from "@mui/material/styles";
 
import { loggedInUserInfoSliceActions } from "./Store/Slices/LoggedInUserInfo";
import RouteTracker from "./Js/RouteTracker";
import MainStageLayout from "./Layouts/MainStage/MainStageLayout";
import { routesConfig, usePayrollApproverRoutes } from "./routes";
import theme from "./theme";
import "react-big-calendar/lib/css/react-big-calendar.css";
 
/* -------- Public Routes (Lazy Loaded) -------- */
const Login = React.lazy(() => import("./Layouts/Login"));
const NewUserPasscodeValidate = React.lazy(() => import("./Layouts/Login/NewUserPasscodeValidate"));
const RecoverPassword = React.lazy(() => import("./Layouts/Login/RecoverPassword"));
const SetCredentials = React.lazy(() => import("./Layouts/Login/SetCredentials"));
const Disclaimer = React.lazy(() => import("./Layouts/Login/Disclaimer"));
const Terms = React.lazy(() => import("./Layouts/Login/Terms"));
const PrivacyPolicy = React.lazy(() => import("./Layouts/Login/PrivacyPolicy"));
const ErrorPage = React.lazy(() => import("./Layouts/Error"));
const Feedback = React.lazy(() => import("./Layouts/FeedbackResponse/FeedbackAnswers"));
const UserRegistrationComponent = React.lazy(() => import("./Layouts/Administrator/Registration/UserRegistrationComponent"));
 
/* -------- Helper: Recursive Route Renderer -------- */
const renderRoutes = (routeArray) => {
  return routeArray.map((route) => (
    <Route key={route.path} path={route.path} element={route.element}>
      {route.children && renderRoutes(route.children)}
    </Route>
  ));
}
 
const App = () => {  
  const dispatch = useDispatch();
  const accessToken = useSelector((s) => s.loggedInUserInfo.accessToken);
  const { routes: userRoutes, loading: routesLoading } =
    usePayrollApproverRoutes(routesConfig);
  const { loader } = useSelector((s) => s.common);
 
  useEffect(() => {
    dispatch(loggedInUserInfoSliceActions.setAccessToken());
  }, [dispatch]);
 
  return (
    <ThemeProvider theme={theme}>
      <StyledEngineProvider injectFirst>
        {/* Global Loader */}
        <Backdrop
          sx={{
            color: "#fff",
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backgroundColor: "rgba(0,0,0,0.2)",
          }}
          open={loader}
        >
          <img src="/cloudload.gif" width="10%" />
        </Backdrop>
 
        <BrowserRouter>
          <RouteTracker />
 
          <Suspense fallback={<div className="loader">Loading...</div>}>
 
            <Routes>
 
              {/* -------- PUBLIC ROUTES -------- */}
              {!accessToken && (
                <>
                  <Route path="/" element={<Login />} />
                  <Route path="NewUserPasscodeValidate" element={<NewUserPasscodeValidate />} />
                  <Route path="RecoverPassword" element={<RecoverPassword />} />
                  <Route path="SetCredentials/:uid">
                    <Route path="" element={<SetCredentials />} />
                    <Route path=":paramusername" element={<SetCredentials />} />
                  </Route>
                  <Route path="Disclaimer" element={<Disclaimer />} />
                  <Route path="Terms" element={<Terms />} />
                  <Route path="PrivacyPolicy" element={<PrivacyPolicy />} />
                  <Route path="privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="privacy" element={<PrivacyPolicy />} />
                  <Route path="Feedback" element={<Feedback />} />
                  <Route path="UserRegistration/:mode" element={<UserRegistrationComponent />} />
                  <Route path="Error" element={<ErrorPage />} />
                </>
              )}
 
              {/* -------- PROTECTED ROUTES -------- */}
              {accessToken && (
                <Route path="/" element={<MainStageLayout />}>
                  {/* Dynamic Role-Based Routes */}
                  {renderRoutes(userRoutes)}
                </Route>
              )}
 
              {/* Fallback */}
              <Route
                path="*"
                element={
                  accessToken && routesLoading ? (
                    <div className="loader">Loading...</div>
                  ) : (
                    <Navigate to="/" />
                  )
                }
              />
 
            </Routes>
 
          </Suspense>
        </BrowserRouter>
      </StyledEngineProvider>
    </ThemeProvider>
  );
};
 
export default App;